import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LeadModal from "@/components/LeadModal";
import StickyCallBar from "@/components/StickyCallBar";
import ExitIntent from "@/components/ExitIntent";
import SeoHead, {
  orgSchema, websiteSchema, faqSchema,
} from "@/components/SeoHead";
import { Building2, Briefcase, Landmark, ShieldCheck, Zap, Clock, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import SocialLinks from "@/components/SocialLinks";
import { SEARCH_MODE, HERO_COPY, HOME_VERTICALS, FUNDING_UNIVERSE, universeUrl, searchUrl } from "@/lib/searchConfig";
import { rememberUniverse } from "@/lib/attribution";
import HiBehindAi from "@/components/HiBehindAi";
import SearchDollarIcon from "@/components/SearchDollarIcon";
import OzyLogoMark from "@/components/OzyLogoMark";
import SiteFooter from "@/components/SiteFooter";

const ICONS = { "commercial-property-loans": Building2, "construction-finance": Landmark, "bridging-finance": Flame, "private-lender-exit": Flame, "private-credit": Landmark, "business-loans": Briefcase, "smsf-property-loans": ShieldCheck };

export default function Landing() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [loanType, setLoanType] = useState("");
  const [description, setDescription] = useState("");
  const [open, setOpen] = useState(false);

  const openWith = (val, type = "", desc = "") => {
    setQuery(val ?? query);
    setLoanType(type);
    setDescription(desc);
    setOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (SEARCH_MODE) {
      if (query.trim().length < 2) return;
      if (window.gtag) window.gtag("event", "ozy_search_submitted", { origin: "homepage" });
      navigate(searchUrl(query));
      return;
    }
    openWith();
  };

  const primaryLoans = HOME_VERTICALS.primary;
  const secondaryLoans = HOME_VERTICALS.secondary;

  const trust = [
    { icon: ShieldCheck, title: "Australian Credit License", desc: "Fully accredited finance specialists operating across Australia." },
    { icon: Zap, title: "Human review", desc: "Experienced finance professionals review your requirements and objectives before options are discussed with you." },
    { icon: Clock, title: "Banks, non-banks & private capital", desc: "Explore funding pathways across banks, non-banks and private capital on our panel." },
  ];

  const seo = SEARCH_MODE ? {
    title: "OzyLoans — Search Finance Across Australia",
    description: "Describe what you need to fund. OZY interprets your scenario and surfaces funding pathways across banks, non-banks and private capital — then the OzyLoans credit team assesses it. ACL #485752.",
    h1Sr: "OzyLoans — Search finance across Australia: banks, non-banks and private capital",
  } : {
    title: "Commercial Mortgages, Development Funding & Bridging Finance Australia",
    description: "OzyLoans — Australian specialists in commercial mortgages, development funding, and bridging & private mortgage solutions. Fast pre-approvals across banks, non-bank and private-credit lenders. ACL #485752.",
    h1Sr: "OzyLoans — Commercial Mortgages, Development Funding & Bridging Finance Australia",
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-outfit relative overflow-hidden">
      <SeoHead
        title={seo.title}
        description={seo.description}
        path="/"
        schemas={[
          orgSchema(),
          websiteSchema(),
          faqSchema([
            ["What does OzyLoans do?", "OzyLoans is an Australian finance search and discovery platform. You describe what you need to fund, OZY interprets the requirement and surfaces funding pathways across banks, non-banks and private capital, and the OzyLoans credit team assesses and arranges suitable finance. ACL #485752."],
            ["Where does OzyLoans operate?", "OzyLoans services clients across all of Australia — Sydney, Melbourne, Brisbane, Perth, Adelaide and every regional postcode in between. Our registered office is in Melbourne but our lender panel and assessments are national."],
            ["Do you charge fees?", "In most cases we are paid a commission by the lender on settlement. Full disclosure is in our Credit Guide."],
          ]),
        ]}
      />
      {/* Top bar */}
      <header className="absolute top-0 left-0 right-0 z-20 px-6 sm:px-10 py-5 flex items-center justify-between">
        <div className="font-bebas text-2xl tracking-[0.25em] text-zinc-400 flex items-center gap-2.5" data-testid="header-wordmark">
          <OzyLogoMark className="w-9 h-9" data-testid="header-brand-mark" />
          <span>OZYLOANS<span className="text-[#E50914]">.</span></span>
        </div>
        <nav className="flex items-center gap-2 sm:gap-4 text-sm">
          {SEARCH_MODE ? (
            <Link to="/about" className="hidden sm:inline text-zinc-400 hover:text-white transition-colors" data-testid="nav-about">About</Link>
          ) : (<>
          <a href="#why" className="hidden sm:inline text-zinc-400 hover:text-white transition-colors">Why us</a>
          <a href="#loans" className="hidden sm:inline text-zinc-400 hover:text-white transition-colors">Loan types</a>
          </>)}
          <SocialLinks size="sm" className="hidden sm:flex" />
        </nav>
      </header>

      {/* HERO – Google-style */}
      <section className={`relative flex flex-col items-center min-h-[100vh] px-4 ${SEARCH_MODE ? "pt-14 md:pt-24 pb-[5.5rem] md:pb-16 justify-start" : "pt-24 pb-16 justify-center"}`}>
        {SEARCH_MODE && <div className="flex-1" aria-hidden="true" />}
        {/* Subtle red radial behind wordmark */}
        <div
          aria-hidden="true"
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(229,9,20,0.22), rgba(229,9,20,0) 70%)" }}
        />

        {SEARCH_MODE ? (
          <p
            data-testid="hero-wordmark"
            aria-label="OzyLoans"
            className="ozy-rise font-bebas text-[20vw] sm:text-[15vw] md:text-[12rem] lg:text-[14rem] leading-[0.85] tracking-[-0.02em] text-[#E50914] wordmark-glow text-center"
            style={{ textShadow: "0 0 40px rgba(229,9,20,0.5), 0 0 80px rgba(229,9,20,0.25)" }}
          >
            <span aria-hidden="true">OZYLOANS</span>
          </p>
        ) : (
        <h1
          data-testid="hero-wordmark"
          className="ozy-rise font-bebas text-[20vw] sm:text-[15vw] md:text-[12rem] lg:text-[14rem] leading-[0.85] tracking-[-0.02em] text-[#E50914] wordmark-glow text-center"
          style={{ textShadow: "0 0 40px rgba(229,9,20,0.5), 0 0 80px rgba(229,9,20,0.25)" }}
        >
          <span className="sr-only">{seo.h1Sr}</span>
          <span aria-hidden="true">OZYLOANS</span>
        </h1>
        )}

        {/* Google-style search bar */}
        <form
          onSubmit={handleSubmit}
          role={SEARCH_MODE ? "search" : undefined}
          className="ozy-rise ozy-rise-delay-2 w-full max-w-2xl lg:max-w-[49rem] mt-6 sm:mt-8"
        >
          <label htmlFor="hero-search-input" className="sr-only">{SEARCH_MODE ? HERO_COPY.label : "How much $ you want to borrow?"}</label>
          <div className="ozy-search w-full h-14 sm:h-16 lg:h-[4.4rem] bg-black/55 backdrop-blur-xl border-2 rounded-full px-5 sm:px-6 flex items-center gap-3">
            <SearchDollarIcon className="w-6 h-6 text-[#E50914] shrink-0" data-testid="hero-search-icon" />
            <input
              id="hero-search-input"
              data-testid="hero-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={SEARCH_MODE ? HERO_COPY.placeholder : "How much $ you want to borrow?"}
              className="flex-1 bg-transparent outline-none text-white placeholder:text-zinc-400 text-base sm:text-lg min-w-0"
            />
            <button
              type="submit"
              data-testid="hero-search-button"
              aria-label={SEARCH_MODE ? HERO_COPY.cta : "Search"}
              className="hidden sm:inline-flex h-9 lg:h-10 px-4 lg:px-5 items-center rounded-full bg-[#E50914] hover:bg-[#F6121D] text-white text-sm font-semibold transition-colors"
            >
              {SEARCH_MODE ? HERO_COPY.cta : "Get funded"}
            </button>
          </div>

          {SEARCH_MODE ? (
            <>
              <Button type="submit" data-testid="hero-search-button-mobile" className="sm:hidden mx-auto mt-4 flex bg-[#E50914] hover:bg-[#F6121D] rounded-full px-6 h-11 font-semibold">
                {HERO_COPY.cta}
              </Button>
              <div className="ozy-rise ozy-rise-delay-3 text-center mt-5 sm:mt-10">
                <HiBehindAi size="hero" className="inline-block" testid="hero-hi-behind-ai" />
                <h1 data-testid="hero-h1" className="text-zinc-200 text-sm sm:text-[1.15rem] font-normal tracking-wide mt-3">
                  {HERO_COPY.sub}
                </h1>
              </div>
            </>
          ) : (
            <>
          <p className="ozy-rise ozy-rise-delay-2 text-center text-zinc-500 text-xs sm:text-sm mt-5 mb-3 tracking-[0.35em] uppercase">
            Funding, found.
          </p>

          <div className="ozy-rise ozy-rise-delay-3 flex flex-wrap items-center justify-center gap-3">
            <Button
              type="button"
              data-testid="cta-quote-button"
              onClick={() => openWith()}
              className="bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white rounded-full px-5 h-11 font-medium backdrop-blur-md"
            >
              Get a quick quote
            </Button>
            <Button
              type="button"
              data-testid="cta-broker-button"
              variant="ghost"
              onClick={() => openWith()}
              className="text-zinc-300 hover:text-white hover:bg-white/[0.05] rounded-full px-5 h-11"
            >
              Speak with a specialist
            </Button>
          </div>

          <p className="text-center text-zinc-600 text-xs mt-7 tracking-wide">
            Press <kbd className="px-1.5 py-0.5 bg-white/[0.04] border border-white/10 rounded text-zinc-400">Enter</kbd> or click any option to start your application.
          </p>
            </>
          )}
        </form>

        {SEARCH_MODE && <div className="flex-1" aria-hidden="true" />}
        {SEARCH_MODE ? (
          <nav
            aria-label="Funding universe"
            data-testid="funding-universe"
            className="ozy-rise ozy-rise-delay-3 mt-auto pt-4 md:pt-6 w-full max-w-2xl mx-auto flex flex-wrap justify-center items-center gap-x-3 gap-y-2 font-bebas text-base sm:text-lg md:text-xl tracking-[0.05em] leading-none"
          >
            {[FUNDING_UNIVERSE.slice(0, 3), FUNDING_UNIVERSE.slice(3)].map((group, g) => (
              <span key={g} className="inline-flex items-center gap-x-3 whitespace-nowrap">
                {group.map((u, i) => (
                  <span key={u.key} className="inline-flex items-center gap-x-3">
                    {(i > 0 || g > 0) && <span aria-hidden="true" className={`text-zinc-800 font-outfit text-sm ${i === 0 ? "hidden md:inline" : ""}`}>|</span>}
                    <Link
                      to={universeUrl(u)}
                      data-testid={`universe-${u.key}`}
                      onClick={() => { rememberUniverse(u.key); if (window.gtag) window.gtag("event", "funding_universe_selected", { funding_universe_category: u.key }); }}
                      className={`${u.tone} hover:text-white transition-colors`}
                    >
                      {u.label}
                    </Link>
                  </span>
                ))}
              </span>
            ))}
          </nav>
        ) : (
        <div className="ozy-rise ozy-rise-delay-3 mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.2em] text-zinc-500">
          <span className="inline-flex items-center gap-1.5"><span className="text-[#E50914] font-bold">Banks · Non-banks · Private capital</span></span>
          <span className="text-zinc-800">·</span>
          <span className="inline-flex items-center gap-1.5">ACL <span className="text-[#E50914] font-bold">#485752</span></span>
          <span className="text-zinc-800">·</span>
          <span className="inline-flex items-center gap-1.5">AFCA Member <span className="text-[#E50914] font-bold">#618333</span></span>
        </div>
        )}
      </section>

      {SEARCH_MODE ? <SiteFooter /> : (<>
      {/* LOAN TYPES */}
      <section id="loans" className="relative border-t border-zinc-900 py-24 sm:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">Loan types</p>
              <h2 className="font-bebas text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95]">
                Anything Australia<br/> borrows for.
              </h2>
            </div>
            <Button
              onClick={() => openWith()}
              data-testid="loans-cta-button"
              className="self-start sm:self-end bg-[#E50914] hover:bg-[#F6121D] rounded-full px-6 h-12 font-semibold"
            >
              Start an enquiry
            </Button>
          </div>

          {/* Vertical tiles — crawlable links into the protected /finance/* hubs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {primaryLoans.map((l) => { const Icon = ICONS[l.slug] || Building2; return (
              <Link
                key={l.slug}
                to={`/finance/${l.slug}`}
                data-testid={l.testid}
                className="text-left group block bg-[#0c0c0c] border-2 border-[#E50914]/60 hover:border-[#E50914] rounded-2xl p-7 hover:bg-[#0f0f0f] transition-all"
              >
                <div className="flex items-center justify-between mb-5">
                  <Icon className="w-6 h-6 text-[#E50914]" />
                  <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.25em] text-[#E50914] bg-[#E50914]/10 border border-[#E50914]/30 rounded-full px-2 py-0.5 font-semibold">
                    Featured
                  </span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{l.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{l.desc}</p>
                <span className="text-zinc-600 group-hover:text-[#E50914] text-xs uppercase tracking-widest mt-5 inline-block transition-colors">
                  {l.cta} →
                </span>
              </Link>
            ); })}
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {secondaryLoans.map((l) => { const Icon = ICONS[l.slug] || Briefcase; return (
              <Link
                key={l.slug}
                to={`/finance/${l.slug}`}
                data-testid={l.testid}
                className="text-left group block bg-[#0c0c0c] border border-zinc-800/70 rounded-2xl p-6 hover:border-[#E50914]/50 hover:bg-[#0f0f0f] transition-all"
              >
                <Icon className="w-5 h-5 text-zinc-400 group-hover:text-[#E50914] mb-4 transition-colors" />
                <h3 className="text-base font-semibold mb-2">{l.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{l.desc}</p>
              </Link>
            ); })}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-zinc-500" data-testid="home-more-verticals">
            <span className="uppercase tracking-[0.25em] text-zinc-600">Also explore</span>
            {HOME_VERTICALS.more.map((m) => (
              <Link key={m.slug} to={`/finance/${m.slug}`} className="hover:text-white underline-offset-4 hover:underline">{m.title}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — platform-led origination */}
      <section id="how-it-works" className="relative border-t border-zinc-900 py-24 sm:py-32 px-6" data-testid="how-it-works">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">How it works</p>
            <h2 className="font-bebas text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95]">
              How OzyLoans<br/>
              <span className="text-zinc-500">works.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {([
              { n: "01", t: "You submit", d: "Tell us the amount, purpose, security and timing in about 60 seconds." },
              { n: "02", t: "We review",  d: "Our team reviews the scenario, checks fit and identifies the strongest funding paths." },
              { n: "03", t: "We route",   d: "Qualified deals are routed to suitable bank, non-bank and private-credit capital partners." },
              { n: "04", t: "You close",  d: "We coordinate the next steps through terms, approvals and settlement." },
            ]).map((step, i) => (
              <div
                key={step.n}
                className="bg-[#0c0c0c] border border-zinc-800/70 rounded-2xl p-7 hover:border-[#E50914]/40 transition-colors"
                data-testid={`step-${i + 1}`}
              >
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#E50914] font-semibold mb-4">{step.n}</p>
                <h3 className="text-lg font-semibold mb-2">{step.t}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="why" className="relative border-t border-zinc-900 py-24 sm:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">Why OzyLoans</p>
            <h2 className="font-bebas text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95]">
              We fight for the deal.<br/>
              <span className="text-zinc-500">You sign the papers.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {trust.map((t) => (
              <div
                key={t.title}
                className="bg-[#0c0c0c] border border-zinc-800/70 rounded-2xl p-7 hover:border-zinc-700 transition-colors"
              >
                <t.icon className="w-6 h-6 text-[#E50914] mb-5" />
                <h3 className="text-lg font-semibold mb-2">{t.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT BAND */}
      <section className="relative border-t border-zinc-900 py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-bebas text-4xl sm:text-5xl md:text-6xl tracking-tight">
            Ready when you are.
          </h2>
          <p className="text-zinc-500 mt-4 max-w-xl mx-auto">
            One short form. Our finance professionals review your requirements and objectives, then discuss the options worth pursuing with you. You decide.
          </p>
          <Button
            onClick={() => openWith()}
            data-testid="bottom-cta-button"
            className="mt-8 bg-[#E50914] hover:bg-[#F6121D] rounded-full px-8 h-14 text-base font-semibold"
          >
            Request a human review
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#050505] text-zinc-400 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Company */}
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">OzyLoans</h3>
              <p className="text-sm leading-6">
                Asian Pacific House<br />
                Level 701/434 St Kilda Rd<br />
                Melbourne VIC 3004
              </p>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="tel:+61426976876" className="hover:text-white transition">
                    +61 426 976 876
                  </a>
                </li>
                <li>
                  <a href="mailto:mark@ozyloans.com.au" className="hover:text-white transition">
                    mark@ozyloans.com.au
                  </a>
                </li>
              </ul>
              <div className="mt-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-2.5">Follow</p>
                <SocialLinks size="md" />
              </div>
            </div>

            {/* Compliance */}
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Compliance</h3>
              <ul className="space-y-2 text-sm">
                <li>Australian Credit Licence #485752</li>
                <li>AFCA Membership #618333</li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/about" data-testid="footer-about-link" className="hover:text-white transition">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/credit-guide" className="hover:text-white transition">
                    Credit Guide
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white transition">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/complaints" className="hover:text-white transition">
                    Complaints &amp; Disputes
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-zinc-900 text-center">
            <p
              data-testid="footer-platform-tagline"
              className="text-zinc-600 text-[10px] sm:text-xs tracking-[0.35em] uppercase mb-3"
            >
              Human-powered finance search. Australia-wide.
            </p>
            <p className="text-xs text-zinc-700">
              © {new Date().getFullYear()} OzyLoans. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      </>)}

      <LeadModal
        open={open}
        onOpenChange={setOpen}
        initialAmount={SEARCH_MODE ? "" : query}
        initialLoanType={loanType}
        initialDescription={SEARCH_MODE && query ? query : description}
        sourceChannel="homepage"
        framing={SEARCH_MODE ? "human_review" : ""}
        searchContext={SEARCH_MODE ? { conversion_intent: "human_review" } : null}
        onSuccess={() => { setQuery(""); setLoanType(""); setDescription(""); }}
      />
      {!SEARCH_MODE && <StickyCallBar onFreeAssessmentClick={() => openWith(undefined, "")} />}
      <ExitIntent onCta={() => openWith(undefined, "")} suppress={open} />
    </div>
  );
}
